import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  WBChip,
  WBLinkButton,
  WBTable,
  WBTableBody,
  WBTableCell,
  WBTableContainer,
  WBTableHead,
  WBTableRow,
  WBTypography,
} from '@admiin-com/ds-web';
import { Paper, styled, useTheme } from '@mui/material';
import { BillCreateFormData } from '../../../../providers/TaskCreationProvider/TaskCreationProvider';
import { CurrencyNumber } from '../../../../../components/CurrencyNumber/CurrencyNumber';
import { enumToCapitalizedString } from '@admiin-com/ds-common';
import { TaskSettlementStatus } from '@admiin-com/ds-graphql';

const StyledTableContainer = styled(WBTableContainer)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  boxShadow: theme.shadows[2],
}));

const StyledTableHead = styled(WBTableHead)(({ theme }) => ({
  // backgroundColor: theme.palette.grey[50],
}));

const StyledTableRow = styled(WBTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ActionCell = styled(WBTableCell)(({ theme }) => ({
  width: '120px',
  textAlign: 'right',
}));

interface TaxReviewTableProps {
  taxes: BillCreateFormData[];
  onEdit: (index: number) => void;
}

export const TaxReviewTable: React.FC<TaxReviewTableProps> = ({
  taxes,
  onEdit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const getEntityName = (entity: any) => {
    if (!entity) return t('notSpecified', { ns: 'common' });
    return (
      entity.name ||
      entity.companyName ||
      entity.firstName + ' ' + entity.lastName ||
      t('unnamed', { ns: 'common' })
    );
  };

  const getPRN = (tax: BillCreateFormData) => {
    return tax.futureTaskInput?.bpayReferenceNumber || '-';
  };

  const getAmount = (tax: BillCreateFormData) => {
    return tax.futureTaskInput?.amount || 0;
  };

  const getSettlementStatusChip = (settlementStatus?: string | null) => {
    if (!settlementStatus) {
      return (
        <WBTypography variant="body2">
          {t('notProvided', { ns: 'common' })}
        </WBTypography>
      );
    }

    const normalizedStatus = settlementStatus.toString().toUpperCase();
    const color =
      normalizedStatus === TaskSettlementStatus.REFUNDABLE
        ? 'success'
        : normalizedStatus === TaskSettlementStatus.PAYABLE
        ? 'warning'
        : normalizedStatus === TaskSettlementStatus.NIL
        ? 'default'
        : 'default';

    return (
      <WBChip
        label={enumToCapitalizedString(settlementStatus)}
        size="small"
        color={color}
      />
    );
  };

  return (
    <StyledTableContainer component={Paper}>
      <WBTable size="medium">
        <StyledTableHead>
          <WBTableRow>
            <WBTableCell>
              <WBTypography variant="subtitle2" fontWeight="bold">
                {t('lodgementReference', { ns: 'taskbox' })}
              </WBTypography>
            </WBTableCell>
            <WBTableCell>
              <WBTypography variant="subtitle2" fontWeight="bold">
                {t('client', { ns: 'taskbox' })}
              </WBTypography>
            </WBTableCell>
            <WBTableCell>
              <WBTypography variant="subtitle2" fontWeight="bold">
                {t('prn', { ns: 'taskbox' })}
              </WBTypography>
            </WBTableCell>
            <WBTableCell>
              <WBTypography variant="subtitle2" fontWeight="bold">
                {t('documentType', { ns: 'taskbox' })}
              </WBTypography>
            </WBTableCell>
            <WBTableCell align="right">
              <WBTypography variant="subtitle2" fontWeight="bold">
                {t('amount', { ns: 'taskbox' })}
              </WBTypography>
            </WBTableCell>
            <WBTableCell>
              <WBTypography variant="subtitle2" fontWeight="bold">
                {t('settlementStatus', { ns: 'taskbox' })}
              </WBTypography>
            </WBTableCell>
            <ActionCell>
              <WBTypography
                variant="subtitle2"
                fontWeight="bold"
                textAlign={'right'}
              >
                {t('actions', { ns: 'common' })}
              </WBTypography>
            </ActionCell>
          </WBTableRow>
        </StyledTableHead>
        <WBTableBody>
          {taxes.map((tax, index) => (
            <StyledTableRow key={index}>
              <WBTableCell>
                <WBTypography variant="body2">
                  {tax.reference || t('untitled', { ns: 'common' })}
                </WBTypography>
              </WBTableCell>
              <WBTableCell>
                <WBTypography variant="body2">
                  {getEntityName(tax.to)}
                </WBTypography>
              </WBTableCell>
              <WBTableCell>
                <WBTypography variant="body2" fontFamily="monospace">
                  {getPRN(tax)}
                </WBTypography>
              </WBTableCell>
              <WBTableCell>
                <WBTypography variant="body2">
                  {tax.documentType || t('notProvided', { ns: 'common' })}
                </WBTypography>
              </WBTableCell>
              <WBTableCell align="right">
                <CurrencyNumber
                  number={getAmount(tax)}
                  variant="body2"
                  sup={false}
                />
              </WBTableCell>
              <WBTableCell>
                {getSettlementStatusChip(tax.settlementStatus)}
              </WBTableCell>
              <ActionCell>
                <WBLinkButton
                  color="primary.main"
                  sx={{
                    textWrap: 'nowrap',
                    textDecoration: 'underline',
                    ...theme.typography.body2,
                  }}
                  onClick={() => onEdit(index)}
                >
                  {t('edit', { ns: 'taskbox' })}
                </WBLinkButton>
              </ActionCell>
            </StyledTableRow>
          ))}
        </WBTableBody>
      </WBTable>
    </StyledTableContainer>
  );
};
